import { createContext, useContext, useEffect, useState } from 'react';

import { Path } from 'pages/Map/MyMap/Map/components/ZoneMap/components/Path';
import { Region, Zone } from 'pages/Map/types';
import { RegionContextProviderType, RegionContextType } from './types';
import { getZoneIndex, updateWithPath } from './utils';
import { MapSearchContext } from 'context/MapSearchContext';
import { useMapParams } from 'hooks/useMapParams';

export const defaultRegionContextType: RegionContextType = {
    region: {} as unknown as Region,
    setRegion: () => {},
    readonly: false,
    currentZone: {} as unknown as Zone,
    isMapBuilder: false,
    updateRegionWithPath: (_zoneName: string, _path: Path) => {}
};

export const RegionContext = createContext<RegionContextType>(defaultRegionContextType);

export const useRegionContext = () => useContext(RegionContext);

/**
 * RegionContextProvider provides region context.
 * Maintains entire region information and currentZone handling
 */
export const RegionContextProvider = ({
    children,
    region: initialRegion,
    isReadonly = false,
    isMapBuilder = false
}: RegionContextProviderType) => {
    const [region, setRegion] = useState<Region>(initialRegion);
    const [readonly] = useState(isReadonly);
    const { zoneName } = useMapParams();
    const { setActiveSearchId } = useContext(MapSearchContext);

    useEffect(() => {
        setRegion(initialRegion);
    }, [initialRegion]);

    const updateRegionWithPath = (zoneName: string, path: Path) => {
        const updatedZone = updateWithPath(region.zones, zoneName, path);
        if (!updatedZone) {
            return;
        }
        setRegion({
            ...region,
            zones: region.zones.map(zone => (zone.name === zoneName ? updatedZone : zone))
        });
    };

    const zoneIndex = getZoneIndex(region, zoneName);
    const currentZone = region.zones[zoneIndex];

    useEffect(() => {
        setActiveSearchId(undefined);
    }, [zoneIndex]);

    return (
        <RegionContext.Provider
            value={{
                region,
                readonly,
                setRegion,
                currentZone,
                isMapBuilder,
                updateRegionWithPath
            }}
        >
            {children}
        </RegionContext.Provider>
    );
};
